import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import NewFormSelect from "@ats/src/components/forms/NewFormSelect";
import Tooltip from "@ats/src/components/shared/Tooltip";
import Icon from "@ats/src/components/shared/Icon";

export interface KnockoutQuestionFilterRowProps {
  knockoutQuestion: {
    questionIds: number[];
    questionText: string;
    correctOption: string;
    otherOptions: string[];
  };
  condition: string;
  onConditionChange: (condition: string) => void;
  onRemove: () => void;
}

const KnockoutQuestionFilterRow: React.FunctionComponent<KnockoutQuestionFilterRowProps> = ({
  knockoutQuestion,
  condition,
  onConditionChange,
  onRemove,
}) => {
  // The hover text differs per condition, so the Tooltip goes on the individual option
  // rather than around the whole select. NewFormSelect renders `customLabel || label`
  // in both its option rows and its selected value when useCustomOptions is on.
  const conditionOptions = [
    {
      value: "pass",
      label: "Pass",
      customLabel: <Tooltip label={knockoutQuestion.correctOption}>Pass</Tooltip>,
    },
    {
      value: "fail",
      label: "Fail",
      customLabel: <Tooltip label={knockoutQuestion.otherOptions.join(", ")}>Fail</Tooltip>,
    },
    { value: "no_reply", label: "No reply" },
  ];

  return (
    <Styled.Container>
      <Styled.Question>{knockoutQuestion.questionText}</Styled.Question>
      <Styled.Controls>
        <Styled.Label>Reply is</Styled.Label>
        <Styled.SelectWrap>
          <NewFormSelect
            onChange={(name, value) => onConditionChange(value)}
            name="condition"
            label=""
            description=""
            className=""
            value={condition}
            options={conditionOptions}
            errors={null}
            useCustomOptions={true}
          />
        </Styled.SelectWrap>
        <Styled.Remove type="button" onClick={onRemove} aria-label="Remove this filter">
          <Icon name="x" />
        </Styled.Remove>
      </Styled.Controls>
    </Styled.Container>
  );
};

export default KnockoutQuestionFilterRow;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: KnockoutQuestionFilterRow;
    ${[t.px(4), t.py(3)]}
    border-bottom: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};

    &:last-of-type {
      border-bottom: none;
    }
  `;
});

Styled.Question = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    label: KnockoutQuestionFilterRow_Question;
    ${[t.text.sm, t.mt(0), t.mb(3)]}
    line-height: 1.25rem;
    color: ${t.dark ? t.color.gray[200] : t.color.black};
  `;
});

Styled.Controls = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: KnockoutQuestionFilterRow_Controls;
    display: flex;
    align-items: center;
    gap: ${t.spacing[2]};
  `;
});

Styled.Label = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: KnockoutQuestionFilterRow_Label;
    ${t.text.sm}
    flex-shrink: 0;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.SelectWrap = styled.div`
  label: KnockoutQuestionFilterRow_SelectWrap;
  width: 9.375rem;
  flex-shrink: 0;

  /* NewFormSelect carries form-field bottom margin; not wanted inline */
  > div {
    margin-bottom: 0;
  }
`;

Styled.Remove = styled.button((props: any) => {
  const t: any = props.theme;
  return css`
    label: KnockoutQuestionFilterRow_Remove;
    ${[t.h(7), t.w(7), t.rounded.sm]}
    margin-left: auto;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    appearance: none;
    background: none;
    cursor: pointer;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    transition: background-color 0.2s ease, color 0.2s ease;

    svg {
      height: 0.9375rem;
      width: 0.9375rem;
    }

    &:hover {
      background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
      color: ${t.dark ? t.color.white : t.color.black};
    }
  `;
});
